<template>
  <div class="containerBox">
    <p class="title">{{ $t('AccountInfo.AccountAuthen.accountInfo') }}</p>
    <div class="list">
      <div class="item">
        <div class="info">
          <div class="main">
            <span class="type">{{ $t('AccountInfo.AccountAuthen.companyInfo') }} </span>
            <span class="hint">{{ $t('AccountInfo.AccountAuthen.companyAction') }}</span>
          </div>
          <span class="sub">{{ $t('AccountInfo.AccountAuthen.adaptCompanyUser') }}</span>
        </div>
        <div v-if='companyState.state == "authenticated"'
             class="authenticated">
          <check-circle-outlined class="icon" />
          <span class="hint">{{ $t('AccountInfo.AccountAuthen.verified') }}</span>
        </div>
        <div v-else
             class="other-state">
          <question-circle-outlined v-if='companyState.state == "failed"' />
          <span v-if='companyState.state == "failed"'
                class="hint">{{ $t('AccountInfo.AccountAuthen.verifiedFailed') }}</span>
          <a-button :class="companyState.state"
                    type="primary"
                    :disabled='companyState.state == "authenticating"'
                    @click="authenRightNow('company')">{{ companyState.name }}</a-button>
        </div>
      </div>
      <div class="item">
        <div class="info">
          <div class="main">
            <span class="type">{{ $t('AccountInfo.AccountAuthen.userVerified') }}</span>
          </div>
          <span class="sub">{{ $t('AccountInfo.AccountAuthen.adaptUser') }}</span>
        </div>
        <div v-if='userState.state == "authenticated"'
             class="authenticated">
          <check-circle-outlined class="icon" />
          <span class="hint">{{ $t('AccountInfo.AccountAuthen.verified') }}</span>
        </div>
        <div v-else
             class="other-state">
          <question-circle-outlined v-if='companyState.state == "failed"' />
          <span v-if='companyState.state == "failed"'
                class="hint">{{ $t('AccountInfo.AccountAuthen.verifiedFailed') }}</span>
          <a-button :class="userState.state"
                    type="primary"
                    :disabled='companyState.state == "authenticating"'
                    @click="authenRightNow('user')">{{ userState.name }}</a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CORE_ROUTER } from '@/core'
import { container } from 'tsyringe'
import { onMounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Router } from 'vue-router'
import { authCompanyState, authUserState } from '../api/Auth'
import type { CompanyRegisterState, UserRegisterState } from '../api/interface/AuthInterface'

const { t } = useI18n()
const companyState = reactive<{ name: string, state: string }>({ name: t('AccountInfo.AccountAuthen.verifiedNow'), state: 'authenticate' })
const userState = reactive<{ name: string, state: string }>({ name: t('AccountInfo.AccountAuthen.verifiedNow'), state: 'authenticate' })

onMounted(() => {
  authCompanyState().then(result => authenGetState(result as CompanyRegisterState, 'company'))
  authUserState().then(result => authenGetState(result as UserRegisterState, 'user'))
})

const router = container.resolve<Router>(CORE_ROUTER)

// declare Method
const authenRightNow = (state: 'company' | 'user') => {
  switch (state) {
    case 'company':
      router.push("profile/companyAccount"); break
    case 'user':
      router.push("profile/userAccount"); break
  }
}
const authenGetState = (result: UserRegisterState | CompanyRegisterState, markInfo: 'company' | 'user') => {
  const { status } = result
  switch (status) {
    case 'authenticating':
      if (markInfo == 'company') {
        companyState.name = t('AccountInfo.AccountAuthen.reviewing')
        companyState.state = 'authenticating'
      } else {
        userState.name = t('AccountInfo.AccountAuthen.reviewing')
        userState.state = 'authenticating'
      }
      break
    case 'authenticated':
      if (markInfo == 'company') {
        companyState.name = t('AccountInfo.AccountAuthen.verifiedSuccess')
        companyState.state = 'authenticated'
      } else {
        userState.name = t('AccountInfo.AccountAuthen.verifiedSuccess')
        userState.state = 'authenticated'
      }
      break
    case 'failed':
      if (markInfo == 'company') {
        companyState.name = t('AccountInfo.AccountAuthen.initVerified')
        companyState.state = 'failed'
      } else {
        userState.name = t('AccountInfo.AccountAuthen.initVerified')
        userState.state = 'failed'
      }
      break
  }
}
</script>

<style lang="less" scoped>
.containerBox {
  text-align: left;
  font-family: 'Microsoft YaHei';

  .title {
    padding: 10px 0;
    font-size: 16px;
    font-family: 'Microsoft YaHei';
    font-weight: 400;
    color: #111111;
  }

  .list {
    display: flex;
    justify-content: space-between;
    gap: 10px;

    .item {
      height: 124px;
      width: 50%;
      display: inline-flex;
      align-items: center;
      padding-left: 32px;
      background: #FFFFFF;
      border-radius: 4px;
      box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
      background: url("./assets/imgs/auth_bg.png") right no-repeat;

      .info {
        display: flex;
        flex-direction: column;
        font-size: 16px;
        font-family: 'Microsoft YaHei';
        font-weight: 400;
        color: rgba(0, 0, 0, 0.8500);
        line-height: 24px;
        flex: 1;

        .sub {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.4500);
        }

        .main {
          .type {
            display: inline-flex;
          }

          .hint {
            width: 100px;
            margin-left: 8px;
            height: 24px;
            background: #3065ED;
            border-radius: 2px;
            text-align: center;
            border: 1px solid #3065ED;
            font-size: 12px;
            font-family: 'Microsoft YaHei';
            font-weight: 400;
            color: white;
            line-height: 22px;
            padding: 2px 4px;
          }
        }
      }

      .authenticated {
        display: flex;
        flex-direction: column;
        gap: 9px;
        margin: 50px;

        .icon {
          font-size: 40px;
          font-weight: 100;
          color: #999999;
        }

        .hint {
          font-size: 14px;
          font-family: 'Microsoft YaHei';
          font-weight: 400;
          color: #999999;
        }
      }

      .other-state {
        display: flex;
        align-items: center;

        .hint,
        span {
          font-size: 14px;
          font-family: 'Microsoft YaHei';
          font-weight: 400;
          color: #DE4A09;
        }

        .hint {
          margin: 5px 30px 5px 5px;
        }

        button {
          width: 120px;
          height: 40px;
          // color: white;
          // background-color: #111111;
          font-size: 14px;
          font-family: 'Microsoft YaHei';
          font-weight: 400;
          margin: 50px 50px 50px 0;
        }

        .authenticating {
          background-color: #999999;
        }
      }
    }
  }
}
</style>
